import { Suspense, useState } from 'react';
import { AgendaTable } from '../AgendaTable';
import { MeetingProtocolEdit } from '../../ProtocolEdit';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Trans } from '@lingui/react';
import { AgendaItemAddDialog } from './AgendaItemAddDialog';
import { initial, range } from 'lodash-es';
import {
  GqlMeetingParticipant,
  GqlAgendaItem,
  GqlAgendaItemDecision,
  GqlMeeting,
} from '../../../../../graphql/base-types.graphql';
import { AiMcProps } from '../../agenda-items/MeetingConcern';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { BoardMeetingDocument } from '../../../../board-meetings/BoardMeeting.graphql';
import { ShareholdersMeetingDocument } from '../../../../shareholders-meetings/ShareholdersMeeting.graphql';
import { useDeleteAiMutation, useSetAiOrderMutation } from '../../../../../graphql/react-operations';

export const AgendaEdit = ({
  meeting,
  meetingConcerns,
  agendaItems,
}: {
  meeting: Pick<
    GqlMeeting,
    'id' | 'primaryLang' | 'secondaryLang' | 'boardsRecommendation' | 'companyId' | 'startTime' | 'kind'
  > & {
    meetingParticipants: (Pick<GqlMeetingParticipant, 'id' | 'present' | 'chairman' | 'secretary' | 'adjuster'> & {
      identity: DisplayIdentity;
      proxy?: DisplayIdentity | null;
    })[];
  };
  meetingConcerns: AiMcProps[];
  agendaItems: (Pick<
    GqlAgendaItem,
    | 'id'
    | 'kind'
    | 'firstGroup'
    | 'lastGroup'
    | 'order'
    | 'canHaveBoardsRecommendation'
    | 'commentPrimaryLang'
    | 'commentSecondaryLang'
    | 'mandatory'
    | 'done'
    | 'subjectPrimaryLang'
    | 'subjectSecondaryLang'
  > & {
    agendaItemDecisions: Pick<
      GqlAgendaItemDecision,
      'id' | 'order' | 'decisionPrimaryLang' | 'decisionSecondaryLang'
    >[];
  })[];
}) => {
  const [showAddDialog, setShowAddDialog] = useState(false);

  const lastFirstGroupOrder = agendaItems.findLast(ai => ai.firstGroup)!.order + 1;
  const firstLastGroupOrder = agendaItems.find(ai => ai.lastGroup)!.order + 1;
  const availableRange = range(lastFirstGroupOrder, firstLastGroupOrder);
  const movableRange = initial(availableRange);
  const [doDelete, { loading: deleteLoading }] = useDeleteAiMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument],
  });
  const [doMove, { loading: moveLoading }] = useSetAiOrderMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument],
  });

  return (
    <>
      <Stack gap={1}>
        <Box>
          <Button variant="outlined" onClick={() => setShowAddDialog(true)}>
            <Trans id="Add Agenda Item" />
          </Button>
        </Box>

        {!meeting.boardsRecommendation ? (
          <Suspense>
            <AgendaTable
              secondaryLang={meeting.secondaryLang}
              agendaItems={agendaItems}
              movableRange={movableRange}
              doDelete={id => doDelete({ variables: { companyId: meeting.companyId, id } })}
              doMove={(id, order) => doMove({ variables: { companyId: meeting.companyId, input: { id, order } } })}
              loading={deleteLoading || moveLoading}
            />
          </Suspense>
        ) : (
          <MeetingProtocolEdit
            meeting={meeting}
            meetingConcerns={meetingConcerns}
            agendaItems={agendaItems}
            isBoardsRecommendation
            movableRange={movableRange}
          />
        )}
      </Stack>
      {showAddDialog && (
        <AgendaItemAddDialog
          meeting={meeting}
          agendaItems={agendaItems}
          availableRange={availableRange}
          onClose={() => setShowAddDialog(false)}
        />
      )}
    </>
  );
};
